import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  Divider,
  DialogActions,
} from '@mui/material';
import { ImageUpload } from '../../atoms/UploadImage';
import axios from 'axios';
import { AddDocument } from '../../../services/technician';
import useSnackbar from '../../../hooks/useSnackbar';
import { useAuth } from '../../../store/AuthContext';
import { Close } from '@mui/icons-material';
import { PrimaryButton, SecondaryButton } from '../../atoms/Button';

interface DocumentUploadProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  onReload: () => void;
  technicianId: string;
}

const documentOptions = [
  // { value: 'AADHAR', label: 'Aadhaar Card' },
  // { value: 'PAN', label: 'PAN Card' },
  // { value: 'PASSPORT', label: 'Passport' },
  // { value: 'VOTER_ID', label: 'Voter ID' },
  // { value: 'DRIVING_LICENSE', label: 'Driving License' },
  { value: 'AADHAR_FRONT', label: 'Aadhar front' },
  { value: 'AADHAR_BACK', label: 'Aadhar back' },
];

export const DocumentUploadModal: React.FC<DocumentUploadProps> = ({
  open,
  onClose,
  onSubmit,
  onReload,
  technicianId,
}) => {
  const { authState } = useAuth();
  const [documentType, setDocumentType] = useState('');
  const [file, setFile] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const handleFileChange = (url: string) => {
    setFile(url);
  };

  const handleSubmit = async () => {
    if (!documentType || !file || !technicianId) {
      showSnackbar(
        'Please select a document type and upload a document.',
        'warning',
      );
      return;
    }
    setLoading(true);
    const data = {
      technicianId: technicianId, // replace with actual technicianId
      comment: comment || `Uploaded  by ${authState.userName}`,
      type: documentType,
      docUrl: file,
    };

    try {
      const responseStatus = await AddDocument(data);
      if (responseStatus === 200) {
        showSnackbar('Document added successfully.', 'success');
        resetDocument();
        setTimeout(() => {
          onReload(); // Reload data from props after submitting
          onClose();
          setLoading(false); // Close drawer after submitting
        }, 800);
      }
    } catch (error) {
      setLoading(false);
      console.error('Error adding document:', error);
      showSnackbar('Failed to add document. Please try again.', 'error');
    }
  };

  const resetDocument = () => {
    setDocumentType('');
    setFile(null);
    setComment('');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{ borderRadius: 10, overflow: 'hidden' }} // Ensuring rounded corners
      PaperProps={{
        sx: {
          borderRadius: 10,
          overflow: 'hidden', // Ensures the background doesn't leak
        },
      }}
    >
      <Box
        sx={{
          border: '1px solid #E31E25', // Add border here
          borderRadius: 10,
          overflow: 'hidden', // Prevent border from affecting layout
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '22px',
            fontWeight: 'bold',
            color: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#181818',
            padding: '16px',
          }}
        >
          {/* {formData.id ? 'Edit User' : 'Create Technician'} */}
          Upload Documents
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </DialogTitle>{' '}
        <Divider />
        <DialogContent sx={{ backgroundColor: '#181818', padding: '24px' }}>
          <Box component="form" sx={{ flex: 1 }}>
            {SnackbarComponent}
            <TextField
              select
              label="Document Type"
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
            >
              {documentOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Comment"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              sx={{ mb: 2 }}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />

            <ImageUpload
              imageUrl={file}
              onChange={handleFileChange}
              setLoader={setLoading}
              onRemove={() => setFile(null)}
              uploadLabel="Upload Document Image"
              fileId="document-upload-image"
              type="banner"
              folderType={4}
              disabled={false}
            />
          </Box>
        </DialogContent>{' '}
        <DialogActions sx={{ backgroundColor: '#181818', padding: '16px' }}>
          <DialogActions sx={{ backgroundColor: '#181818', padding: '16px' }}>
            <SecondaryButton title="Cancel" onClick={onClose} />
            <PrimaryButton
              title={'Submit'}
              disabled={loading}
              onClick={handleSubmit}
            />
          </DialogActions>
        </DialogActions>{' '}
      </Box>
    </Dialog>
  );
};
